import { useEffect, useRef, useState } from "react";
import { haptics } from "@/lib/haptics";

type Options = {
  onRefresh: () => void | Promise<unknown>;
  enabled?: boolean;
  threshold?: number;
  target?: HTMLElement | null;
};

/**
 * Native-style pull-to-refresh. Attaches passively to a scrollable target
 * (defaults to the nearest scroll container or window). Only triggers when
 * the target is scrolled to the top and the user pulls down past `threshold`.
 */
export function usePullToRefresh({
  onRefresh,
  enabled = true,
  threshold = 70,
  target,
}: Options) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const armedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (!("ontouchstart" in window)) return;

    const el: HTMLElement | Window = target ?? window;
    const getScrollTop = () => {
      if (target) return target.scrollTop;
      return window.scrollY || document.documentElement.scrollTop;
    };

    const onStart = (e: TouchEvent) => {
      if (refreshing) return;
      if (getScrollTop() > 0) {
        startY.current = null;
        return;
      }
      startY.current = e.touches[0]?.clientY ?? null;
      armedRef.current = true;
    };

    const onMove = (e: TouchEvent) => {
      if (!armedRef.current || startY.current === null) return;
      const y = e.touches[0]?.clientY ?? 0;
      const dy = y - startY.current;
      if (dy <= 0) {
        setPullDistance(0);
        return;
      }
      // dampened pull
      setPullDistance(Math.min(threshold * 1.8, dy * 0.5));
    };

    const onEnd = async () => {
      if (!armedRef.current) return;
      armedRef.current = false;
      const reached = pullDistance >= threshold;
      if (reached) {
        haptics.trigger("medium");
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
        }
      }
      setPullDistance(0);
      startY.current = null;
    };

    const opts: AddEventListenerOptions = { passive: true };
    (el as HTMLElement).addEventListener("touchstart", onStart as EventListener, opts);
    (el as HTMLElement).addEventListener("touchmove", onMove as EventListener, opts);
    (el as HTMLElement).addEventListener("touchend", onEnd as EventListener, opts);
    return () => {
      (el as HTMLElement).removeEventListener("touchstart", onStart as EventListener);
      (el as HTMLElement).removeEventListener("touchmove", onMove as EventListener);
      (el as HTMLElement).removeEventListener("touchend", onEnd as EventListener);
    };
  }, [enabled, onRefresh, threshold, target, pullDistance, refreshing]);

  return { pullDistance, refreshing };
}

export default usePullToRefresh;
